import { Grid, Step as MuiStep, StepLabel, Stepper } from '@material-ui/core'
import { Button } from 'components/facets'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BulkSettings,
  IdGame,
  PriceStategyTarget,
  PricingStrategy,
  PRICING_BOT_SOURCE,
} from 'shared'
import { OptionBox } from '../OptionBox'
import { FinalizePresenter } from './FinalizePresenter'
import { ModifierPresenter } from './ModifierPresenter'

export interface PriceGuideFlowPresenterProps {
  newName: string
  onRestart: () => void
  bulkSettings: BulkSettings[]
  onDone: (strategy: PricingStrategy) => void
  dataTestid?: string
}

enum Step {
  Base = 1,
  Modifier,
  Finalize,
}

const PLACEHOLDER = '—'

const StrategyPreview = ({
  strategy,
  step,
  progress,
}: {
  strategy: PricingStrategy
  step: Step
  progress: Step
}): JSX.Element => {
  const { t } = useTranslation()
  const parameterTouched = progress > Step.Base
  const modifierTouched = progress > Step.Modifier
  const nameTouched = progress >= Step.Finalize

  const fieldClass = (touched: boolean, isActive: boolean): string => {
    const classes = ['wizard-preview__field']
    if (!touched) classes.push('wizard-preview__field--untouched')
    if (isActive) classes.push('wizard-preview__field--active')
    return classes.join(' ')
  }

  const isPercent = strategy.modifier.kind === 'percent'
  const modifierValue = Math.abs(strategy.modifier.value)
  const modifierUnit = isPercent ? '%' : modifierValue === 1 ? 'cent' : 'cents'
  const modifierLabel = modifierTouched
    ? `${modifierValue}${isPercent ? modifierUnit : ` ${modifierUnit}`}`
    : PLACEHOLDER
  const lessOrMoreLabel =
    modifierTouched && !isPercent
      ? strategy.modifier.value > 0
        ? t('strategy.less')
        : t('strategy.more')
      : null
  const connector = modifierTouched && isPercent ? t('strategy.ofThe') : t('strategy.thanThe')
  const parameterLabel = parameterTouched ? String(strategy.base.tactic.parameter) : PLACEHOLDER

  return (
    <div>
      <h6
        className={`wizard-preview__title ${
          step === Step.Finalize ? 'wizard-preview__field--active' : ''
        }`}
      >
        {nameTouched ? strategy.name || PLACEHOLDER : PLACEHOLDER}
      </h6>
      <p className="wizard-preview__sentence">
        {step >= Step.Modifier && (
          <>
            <span className={fieldClass(modifierTouched, step === Step.Modifier)}>
              {modifierLabel}
            </span>
            {lessOrMoreLabel && (
              <>
                {' '}
                <span className={fieldClass(modifierTouched, step === Step.Modifier)}>
                  {lessOrMoreLabel}
                </span>
              </>
            )}{' '}
            {connector}{' '}
          </>
        )}
        {t('strategy.value')} {t('strategy.of')}{' '}
        <span className={fieldClass(parameterTouched, step === Step.Base)}>{parameterLabel}</span>
      </p>
    </div>
  )
}

export const PriceGuideFlowPresenter = ({
  newName,
  onRestart,
  bulkSettings,
  onDone,
  dataTestid,
}: PriceGuideFlowPresenterProps): JSX.Element => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>(Step.Base)
  const [progress, setProgress] = useState<Step>(Step.Base)
  // Clicking a parameter only selects it; the user advances with the
  // always-visible Next button, disabled until a real choice is made.
  const [parameterSelected, setParameterSelected] = useState(false)

  const [strategy, setStrategy] = useState<PricingStrategy>({
    _id: '2',
    name: newName,
    base: {
      kind: 'priceGuide',
      tactic: { parameter: 'AVG7' },
    },
    modifier: {
      kind: 'percent',
      value: 90,
    },
    bulkSettingsId: {
      [IdGame.Magic]: '',
      [IdGame.Yugioh]: '',
      [IdGame.Pokemon]: '',
      [IdGame.Vanguard]: '',
      [IdGame.DBS]: '',
      [IdGame.FF]: '',
      [IdGame.WS]: '',
      [IdGame.FAB]: '',
      [IdGame.Digimon]: '',
    },
    source: PRICING_BOT_SOURCE.CARDMARKET,
    target: PriceStategyTarget.sellPrice,
  })

  const stepLabels = [t('strategy.parameter'), t('strategy.modifier'), t('strategy.name')]

  const advance = (current: Step): void => {
    setStep(current + 1)
    setProgress(Math.max(current + 1, progress))
  }

  const stepBase = (): JSX.Element => (
    <>
      <h6 className="m-0 p-0 mb-2">{t('strategy.create.parameterDescription')}</h6>
      <p className="mb-1">{t('strategy.create.parameterDescription2')}</p>
      <p className="mb-3">{t('strategy.create.parameterDescription3')}</p>
      <Grid container>
        {['Trend', 'AVG1', 'AVG7', 'AVG30'].map((param) => (
          <Grid xs={6} key={param} item data-testid={`box-${param}`}>
            <OptionBox
              recommended={param === 'AVG7'}
              selected={parameterSelected && strategy.base.tactic.parameter === param}
              onClick={() => {
                const strategyCopy = { ...strategy }
                strategyCopy.base.tactic.parameter = param
                setStrategy(strategyCopy)
                setParameterSelected(true)
              }}
              title={param}
              width="160px"
              dataTestid={dataTestid + `-${param}`}
            />
          </Grid>
        ))}
      </Grid>
    </>
  )

  const stepModifier = (): JSX.Element => (
    <ModifierPresenter
      bulkSettings={bulkSettings}
      strategy={strategy}
      onChange={(s) => setStrategy(s)}
    />
  )

  const stepFinalize = (): JSX.Element => (
    <FinalizePresenter strategy={strategy} setStrategy={setStrategy} />
  )

  const fullWidthStep = step === Step.Modifier

  const nextConfig = ((): {
    onClick: () => void
    isLast?: boolean
    disabled?: boolean
  } | null => {
    switch (step) {
      case Step.Base:
        return {
          onClick: () => advance(Step.Base),
          disabled: !parameterSelected,
        }
      case Step.Modifier:
        return { onClick: () => advance(Step.Modifier) }
      case Step.Finalize:
        return {
          onClick: () => onDone(strategy),
          isLast: true,
          disabled: strategy.name.trim() === '',
        }
      default:
        return null
    }
  })()

  return (
    <div className="p-3 mb-5">
      <button
        type="button"
        onClick={onRestart}
        className="wizard-back mb-2"
        data-testid="restart-strategy-btn"
      >
        ← {t('strategy.create.changeType')}
      </button>
      <Stepper activeStep={step - 1} alternativeLabel className="wizard-stepper">
        {stepLabels.map((label, index) => {
          const stepNumber = index + 1
          const canNavigate = progress >= stepNumber
          return (
            <MuiStep key={label}>
              <StepLabel
                style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                onClick={() => canNavigate && setStep(stepNumber)}
                data-testid={`step-${index}`}
              >
                {label}
              </StepLabel>
            </MuiStep>
          )
        })}
      </Stepper>
      <Grid container spacing={4} className="h-100 mt-3">
        <Grid item xs={fullWidthStep ? 12 : 7}>
          <div className="pt-2">
            {step === Step.Base && stepBase()}
            {step === Step.Modifier && stepModifier()}
            {step === Step.Finalize && stepFinalize()}
          </div>
        </Grid>
        {!fullWidthStep && (
          <Grid item xs={5} className="d-flex flex-column justify-content-start mt-4 h-100">
            <div className="wizard-preview">
              <div className="wizard-preview__label">{t('competitor.create.previewLabel')}</div>
              <StrategyPreview strategy={strategy} step={step} progress={progress} />
            </div>
          </Grid>
        )}
      </Grid>
      {nextConfig && (
        <div className="d-flex justify-content-end mt-3">
          <Button
            disabled={nextConfig.disabled}
            onClick={nextConfig.onClick}
            variant="contained"
            data-testid="next-btn"
          >
            {t(`common.${nextConfig.isLast ? 'done' : 'next'}`)}
          </Button>
        </div>
      )}
    </div>
  )
}
